import type { SearchQueryBody } from "@/types/search";

import { HttpClient, robotoHeaders, PaginatedResponse } from "../../http";

import {
  AgentThreadArtifactUrlResponse,
  AgentThreadContent,
  AgentThreadMessage,
  AgentThreadRecord,
  AgentThreadRecordDelta,
  AgentThreadRole,
  AgentThreadToolDetailResponse,
  ClientToolSpec,
  ClientViewingContext,
  FeedbackCategory,
  FeedbackSentiment,
  isAgentThreadTextContent,
  type ModelProfileResponse,
  SendMessageRequest,
  StartAgentThreadRequest,
  SubmitToolResultsRequest,
  ThreadVisibility,
  UserFeedbackRecord,
} from "./AgentThreadRecord";

interface Options {
  abortSignal: AbortSignal;
  orgId: string;
}

/**
 * Service for interacting with thread-related API endpoints.
 *
 * Provides methods for starting new thread sessions and retrieving thread records.
 */
export class AgentThreadService {
  #httpClient: HttpClient;

  constructor(httpClient: HttpClient) {
    this.#httpClient = httpClient;
  }

  /**
   * Start a new thread session.
   *
   * Single entry point for every "start a thread" trigger — typed message,
   * skill-seeded, or any combination thereof. The backend's
   * StartAgentSessionRequest validator requires at least one of
   * ``message`` / ``invokeSkills`` (or ``goals``, which is currently SDK-only)
   * to be present; the server returns 4xx if neither is.
   *
   * Signature mirrors ``sendMessage`` (positional primary + options bag) so the
   * call shape stays consistent across the service.
   */
  public async startAgentThread(
    message: string | undefined,
    options?: Partial<
      {
        invokeSkills: { skill_id: string; version?: number | null }[];
        clientContext: ClientViewingContext;
        modelProfile: string;
        clientTools: ClientToolSpec[];
      } & Options
    >,
  ): Promise<AgentThreadRecord> {
    if (!options?.orgId) {
      throw Error("startAgentThread requires an Org ID, none was provided");
    }

    const messages: AgentThreadMessage[] = [];
    if (message !== undefined) {
      const agentThreadMessage: AgentThreadMessage = {
        role: AgentThreadRole.USER,
        content: [{ text: message }],
      };
      messages.push(agentThreadMessage);
    }

    const request: StartAgentThreadRequest = {
      messages,
      client_context: options.clientContext,
      model_profile: options.modelProfile,
      client_tools: options.clientTools,
      invoke_skills: options.invokeSkills,
    };

    const requestUrl = this.#httpClient.constructUrl("v1/ai/threads");

    // Non-idempotent: each call mints a new thread_id. A retry on 502
    // would create a duplicate session if the original write had landed.
    const response = await this.#httpClient.post(requestUrl, {
      signal: options.abortSignal,
      headers: robotoHeaders({ orgId: options.orgId }),
      body: JSON.stringify(request),
      idempotent: false,
    });

    return await response.json<AgentThreadRecord>();
  }

  /**
   * Get a thread record by its ID.
   *
   * @param threadId - The unique identifier of the thread session
   * @param loadMessages - Whether to load the full message history (default: true)
   * @param options - Optional configuration including abort signal
   * @returns Promise resolving to the AgentThreadRecord
   */
  public async getAgentThreadById(
    threadId: string,
    loadMessages: boolean = true,
    options?: Partial<Options>,
  ): Promise<AgentThreadRecord> {
    const searchParams = new URLSearchParams();
    searchParams.append("load_messages", loadMessages.toString());

    const requestUrl = this.#httpClient.constructUrl(
      `v1/ai/threads/${threadId}`,
      searchParams,
    );

    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
    });

    return await response.json<AgentThreadRecord>();
  }

  public async getToolDetails(
    threadId: string,
    toolUseId: string,
    options?: Partial<Options>,
  ): Promise<AgentThreadToolDetailResponse> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/ai/threads/${threadId}/tool-use/${toolUseId}`,
    );

    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
    });

    return response.json<AgentThreadToolDetailResponse>();
  }

  /**
   * Get a short-TTL presigned download URL for one artifact a tool call produced. 404s for
   * non-uploaded or expired (30-day lifecycle) artifacts; treat that as "no longer available".
   *
   * @param threadId - The thread the tool call belongs to.
   * @param toolUseId - The tool call that produced the artifact.
   * @param artifactName - The artifact's `name`, as reported in the tool result's `artifacts` list.
   * @param options - Optional configuration including abort signal.
   * @returns The presigned URL, content type, and size of the artifact.
   */
  public async getArtifactUrl(
    threadId: string,
    toolUseId: string,
    artifactName: string,
    options?: Partial<Options>,
  ): Promise<AgentThreadArtifactUrlResponse> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/ai/threads/${threadId}/tool-use/${toolUseId}/artifacts/${encodeURIComponent(artifactName)}`,
    );

    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
    });

    return response.json<AgentThreadArtifactUrlResponse>();
  }

  /**
   * Read an artifact's bytes from a presigned URL minted by {@link getArtifactUrl}.
   *
   * Plain `fetch`, not `HttpClient`: S3 must not receive the API's bearer token. Cross-origin, so
   * it needs `SandboxBucket`'s CORS rule (`stacks/compute/sandbox-bucket.ts`) to allow this app.
   *
   * @param url - A presigned URL from {@link getArtifactUrl}, used before it expires.
   * @param options - Optional configuration including abort signal.
   * @returns The artifact's bytes.
   * @throws Error if S3 answers with a non-2xx status, such as 403 for an expired link.
   */
  public async getArtifactContent(
    url: string,
    options?: Partial<Options>,
  ): Promise<Blob> {
    const response = await fetch(url, { signal: options?.abortSignal });
    if (!response.ok) {
      throw new Error(`Failed to fetch artifact content (${response.status})`);
    }
    return response.blob();
  }

  /**
   * Combines consecutive text content blocks in a content array.
   * This prevents multiple text chunks from appearing as separate message bubbles.
   */
  private combineConsecutiveTextContent(
    content: AgentThreadContent[],
  ): AgentThreadContent[] {
    const combined: AgentThreadContent[] = [];

    for (const item of content) {
      if (isAgentThreadTextContent(item)) {
        const lastItem = combined[combined.length - 1];
        if (lastItem && isAgentThreadTextContent(lastItem)) {
          // Merge with previous text content
          lastItem.text += item.text;
        } else {
          // Add new text content
          combined.push({ text: item.text });
        }
      } else {
        // Non-text content, add as-is
        combined.push(item);
      }
    }

    return combined;
  }

  public async updateAgentThreadFromDeltas(
    record: AgentThreadRecord,
    options?: Partial<Options>,
  ): Promise<AgentThreadRecord> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/ai/threads/${record.thread_id}/delta`,
      new URLSearchParams({ next_token: record.continuation_token }),
    );

    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
    });

    const delta = await response.json<AgentThreadRecordDelta>();

    // Create a new messages array by copying existing messages
    const updatedMessages = record.messages.map((msg) => ({
      ...msg,
      content: [...msg.content],
    }));

    // Rewind before merging. The merge below EXTENDS the message already held at
    // an index; the server rewinds when a turn was abandoned partway and
    // regenerated onto that same index, so what we hold there belongs to an
    // attempt that no longer exists. Extending it would splice the real reply
    // onto a discarded draft. Truncating first also keeps the
    // `messages[k].message_sequence_num === k` invariant the chat view relies
    // on: the delta that follows refills these slots from their first chunk.
    // Tested with `typeof === "number"` rather than a null/undefined check: the
    // server omits this field on every ordinary delta, but pydantic serializes
    // an unset Optional as JSON `null`, not as an absent key. `null !==
    // undefined` is true and `Math.min(n, null)` is 0, so a loose check
    // truncated the whole array on every poll and left only the newest chunk
    // rendered.
    if (typeof delta.reset_from_message_sequence_num === "number") {
      updatedMessages.length = Math.min(
        updatedMessages.length,
        delta.reset_from_message_sequence_num,
      );
    }

    // `messages_by_idx` is keyed by the backend's absolute, gapless
    // `message_sequence_num`. The entire chat view assumes
    // `messages[k].message_sequence_num === k` — task task-section clustering
    // compares backend boundary seq-nums against array positions (see
    // `taskSections.ts`). So each delta message must land at its ABSOLUTE
    // index, never be bare-`push`ed: a non-contiguous delta (which the
    // optimistic-send path and reset continuation token can produce mid-turn)
    // would otherwise shift every later message and mis-cluster task sections
    // while the agent is generating. We keep `index === seq` true by
    // construction, gap-filling any unprovided slot below the highest index.
    const originalLength = updatedMessages.length;

    // Get sorted indices from messages_by_idx
    const sortedIndices = Object.keys(delta.messages_by_idx)
      .map((idx) => parseInt(idx, 10))
      .sort((a, b) => a - b);

    // Process each message in sorted order
    for (const idx of sortedIndices) {
      const deltaMessage = delta.messages_by_idx[idx];

      if (idx < originalLength) {
        // A message we already held: combine content and merge consecutive
        // text blocks so chunked streaming appends in place (e.g. "Hel" + "lo").
        const combinedContent = this.combineConsecutiveTextContent([
          ...updatedMessages[idx].content,
          ...deltaMessage.content,
        ]);

        updatedMessages[idx] = {
          ...updatedMessages[idx],
          status: deltaMessage.status,
          content: combinedContent,
        };
      } else {
        // New message at an absolute sequence number. Fill any gap between the
        // current end and `idx` with empty placeholders rather than leaving
        // sparse holes (the view maps/indexes the array and would throw on
        // `undefined`). Gaps are transient — the next full fetch rebuilds the
        // array gaplessly — and a placeholder renders nothing.
        for (let gap = updatedMessages.length; gap < idx; gap++) {
          updatedMessages[gap] = { role: deltaMessage.role, content: [] };
        }
        updatedMessages[idx] = deltaMessage;
      }
    }

    // Return a new AgentThreadRecord object with updated fields
    return {
      ...record,
      continuation_token: delta.continuation_token,
      status:
        delta.status !== undefined && delta.status !== null
          ? delta.status
          : record.status,
      // Null or absent clears the held activity (see AgentThreadRecordDelta.activity).
      activity: delta.activity ?? null,
      // Null or absent clears the held notice (see AgentThreadRecordDelta.turn_notice).
      turn_notice: delta.turn_notice ?? null,
      title:
        delta.title !== undefined && delta.title !== null
          ? delta.title
          : record.title,
      messages: updatedMessages,
      // Goals: the BE may emit null, omit the field (undefined), or send an array. Both null
      // and undefined mean "no change, retain the previous snapshot" (e.g. cache-hit polls).
      // Only an array is an authoritative new snapshot (an empty array clears goals).
      goals: delta.goals ?? record.goals,
      // Tasks: identical retain-on-null / replace-on-array semantics as goals.
      tasks: delta.tasks ?? record.tasks,
    };
  }

  /**
   * Send a new message to an existing thread conversation.
   *
   * This will add the user's message to the thread and trigger the assistant
   * to generate a response.
   *
   * @param threadId - The unique identifier of the thread session
   * @param message - The message text to send
   * @param options - Optional configuration including orgId and abort signal
   * @returns Promise that resolves when the message is sent
   * @throws Error if orgId is not provided
   */
  public async sendMessage(
    threadId: string,
    message: string,
    options?: Partial<
      {
        clientContext: ClientViewingContext;
        clientTools: ClientToolSpec[];
      } & Options
    >,
  ): Promise<void> {
    if (!options?.orgId) {
      throw Error("sendMessage requires an Org ID, none was provided");
    }

    const agentThreadMessage: AgentThreadMessage = {
      role: AgentThreadRole.USER,
      content: [{ text: message }],
    };

    const request: SendMessageRequest = {
      message: agentThreadMessage,
      client_context: options?.clientContext,
      client_tools: options?.clientTools,
    };

    const requestUrl = this.#httpClient.constructUrl(
      `v1/ai/threads/${threadId}/messages`,
    );

    await this.#httpClient.post(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ orgId: options.orgId }),
      body: JSON.stringify(request),
    });
  }

  /**
   * The caller's own threads in one org, newest first.
   *
   * The backend scopes the list to the org header this sends, so a user who
   * belongs to several orgs sees the org they are working in rather than one
   * merged list.
   *
   * @param options - `orgId` is required; `pageToken` and `abortSignal` are optional.
   * @returns Promise resolving to a paginated response of AgentThreadRecords
   * @throws Error if orgId is not provided
   */
  public async getAgentThreadsForUser(
    options?: Partial<{ pageToken: string } & Options>,
  ): Promise<PaginatedResponse<AgentThreadRecord>> {
    if (!options?.orgId) {
      throw Error(
        "getAgentThreadsForUser requires an Org ID, none was provided",
      );
    }
    const searchParams = new URLSearchParams();
    if (options.pageToken) {
      searchParams.append("page_token", options.pageToken);
    }

    const requestUrl = this.#httpClient.constructUrl(
      "v1/ai/threads",
      searchParams,
    );

    const response = await this.#httpClient.get(requestUrl, {
      signal: options.abortSignal,
      headers: robotoHeaders({ orgId: options.orgId }),
    });

    return await response.json<PaginatedResponse<AgentThreadRecord>>();
  }

  /**
   * Search agent sessions in the caller's org.
   *
   * Backs the Agents > Sessions tab. The body matches the shape the
   * filter widget emits via `filterToCondition`: a `Condition` tree
   * plus optional sort and pagination. The backend enforces a field
   * whitelist (`thread_id`, `title`, `created`, `created_by`) and a
   * sort whitelist (`created` only); filtering or sorting on any other
   * field is rejected with HTTP 400.
   *
   * The endpoint is org-scoped: the backend binds `org_id` from caller
   * identity, not from the request body, so the `orgId` option here
   * only selects which org header to send.
   *
   * @throws Error synchronously if orgId is not provided. The returned
   * promise rejects on transport failure or on a non-2xx response from
   * the backend (e.g. HTTP 400 when the query references a field
   * outside the filter/sort whitelist).
   */
  public async searchAgentThreads(
    query: SearchQueryBody,
    options?: Partial<Options>,
  ): Promise<PaginatedResponse<AgentThreadRecord>> {
    if (!options?.orgId) {
      throw Error("searchAgentThreads requires an Org ID, none was provided");
    }

    const requestUrl = this.#httpClient.constructUrl("v1/ai/threads/search");

    const response = await this.#httpClient.post(requestUrl, {
      signal: options.abortSignal,
      headers: robotoHeaders({ orgId: options.orgId }),
      body: JSON.stringify(query),
    });

    return response.json<PaginatedResponse<AgentThreadRecord>>();
  }

  /**
   * Run a thread's last turn again after it failed.
   *
   * @param threadId - The thread whose last turn failed
   * @param options - `orgId` is required; `abortSignal` is optional
   * @throws Error if orgId is not provided
   * @throws {RobotoInvalidRequestException} If the thread's latest message is
   * not the notice a failed turn ends with.
   * @throws {RobotoConflictException} If another retry of the same failed turn
   * reached the server first.
   */
  public async retryFailedTurn(
    threadId: string,
    options?: Partial<Options>,
  ): Promise<void> {
    if (!options?.orgId) {
      throw Error("retryFailedTurn requires an Org ID, none was provided");
    }

    const requestUrl = this.#httpClient.constructUrl(
      `v1/ai/threads/${threadId}/retry`,
    );

    const response = await this.#httpClient.post(requestUrl, {
      signal: options.abortSignal,
      headers: robotoHeaders({ orgId: options.orgId }),
      // One attempt: if a first attempt reached the server, sending it again is
      // refused, and the person would be told the retry failed while it runs.
      idempotent: false,
    });
    await response.throwIfError();
  }

  /**
   * Request cancellation of the current generation for a thread.
   *
   * This signals to the backend worker that the user wants to stop the current
   * generation. The worker will gracefully terminate and mark the message as cancelled.
   *
   * @param threadId - The unique identifier of the thread session
   * @param options - Optional configuration including abort signal
   * @returns Promise that resolves when the cancellation request is sent
   */
  public async cancelGeneration(
    threadId: string,
    options?: Partial<Options>,
  ): Promise<void> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/ai/threads/${threadId}/cancel`,
    );

    await this.#httpClient.post(requestUrl, {
      signal: options?.abortSignal,
    });
  }

  /**
   * Get available model profiles for the current deployment.
   */
  public async getModelProfiles(
    options?: Partial<Options>,
  ): Promise<ModelProfileResponse[]> {
    if (!options?.orgId) {
      throw Error("getModelProfiles requires an Org ID, none was provided");
    }

    const requestUrl = this.#httpClient.constructUrl("v1/ai/model-profiles");

    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ orgId: options.orgId }),
    });

    return response.json<ModelProfileResponse[]>();
  }

  /**
   * Fork a thread's history up to a specific message into a new caller-owned thread.
   *
   * Fork access mirrors read access: any member of an ORG-visible thread's org
   * can fork it, while a PRIVATE thread can be forked only by its creator or a
   * Roboto admin. The new thread carries the source thread's org_id (so tool
   * calls resolve against the source org's data), is always created PRIVATE
   * regardless of the source's visibility, and is owned by the caller.
   *
   * @param threadId - Source thread id.
   * @param messageSequenceNum - Highest message sequence number (inclusive) to copy.
   * @param options - Optional abort signal.
   * @returns The newly-created AgentThreadRecord.
   */
  public async forkAgentThread(
    threadId: string,
    messageSequenceNum: number,
    options?: Partial<Options>,
  ): Promise<AgentThreadRecord> {
    if (!options?.orgId) {
      // Match ``startAgentThread`` / ``sendMessage`` — every write method on this
      // class throws when orgId is missing, since the backend's
      // ``REQUIRES_CALLER_ORG`` rule rejects requests without one.
      throw Error("forkAgentThread requires an Org ID, none was provided");
    }
    if (!Number.isInteger(messageSequenceNum) || messageSequenceNum < 0) {
      throw Error(
        `forkAgentThread requires a non-negative integer messageSequenceNum, got ${messageSequenceNum}`,
      );
    }
    const requestUrl = this.#httpClient.constructUrl(
      `v1/ai/threads/${threadId}/fork`,
    );

    const response = await this.#httpClient.post(requestUrl, {
      signal: options.abortSignal,
      headers: robotoHeaders({ orgId: options.orgId }),
      body: JSON.stringify({
        message_sequence_num: messageSequenceNum,
      }),
    });

    return response.json<AgentThreadRecord>();
  }

  /**
   * Re-scope who may read a thread: its creator only, or the whole org.
   *
   * The backend accepts this only from the thread's creator. Roboto admins can
   * read a thread they did not create but cannot re-scope it, so show the
   * control only on the signed-in user's own threads rather than letting them
   * discover the restriction through a 401.
   *
   * @param threadId - Thread whose visibility should change.
   * @param visibility - Read scope the thread should have after the call.
   * @param options - `orgId` is required; `abortSignal` is optional.
   * @throws Error if orgId is not provided
   */
  public async updateThreadVisibility(
    threadId: string,
    visibility: ThreadVisibility,
    options?: Partial<Options>,
  ): Promise<void> {
    if (!options?.orgId) {
      throw Error(
        "updateThreadVisibility requires an Org ID, none was provided",
      );
    }
    const requestUrl = this.#httpClient.constructUrl(
      `v1/ai/threads/${threadId}/visibility`,
    );

    await this.#httpClient.post(requestUrl, {
      signal: options.abortSignal,
      headers: robotoHeaders({ orgId: options.orgId }),
      body: JSON.stringify({ visibility }),
    });
  }

  /**
   * Pin or unpin a thread for the calling user, promoting it in their history.
   *
   * A pin is a personal bookmark nobody else can observe, so anyone who can
   * read the thread can pin it, including a teammate's org-shared thread. That
   * is wider than {@link updateThreadVisibility}, which is creator-only.
   *
   * Sends the target state, not a toggle, so a repeat call from a stale view
   * still converges on what the user asked for.
   *
   * @param threadId - Thread to pin or unpin.
   * @param pinned - Pin state the thread should have after the call.
   * @param options - `orgId` is required; `abortSignal` is optional.
   * @throws Error if orgId is not provided
   */
  public async setThreadPinned(
    threadId: string,
    pinned: boolean,
    options?: Partial<Options>,
  ): Promise<void> {
    if (!options?.orgId) {
      throw Error("setThreadPinned requires an Org ID, none was provided");
    }
    const requestUrl = this.#httpClient.constructUrl(
      `v1/ai/threads/${threadId}/pin`,
    );

    await this.#httpClient.post(requestUrl, {
      signal: options.abortSignal,
      headers: robotoHeaders({ orgId: options.orgId }),
      body: JSON.stringify({ pinned }),
    });
  }

  /**
   * Submit categorized feedback on a specific assistant message.
   *
   * One row per (thread, message, user); resubmitting from the same user
   * overwrites sentiment/categories/notes on the existing row.
   *
   * @param threadId - Thread the message belongs to.
   * @param messageSequenceNum - Zero-indexed position of the assistant message.
   * @param args.sentiment - Overall rating direction.
   * @param args.categories - Zero or more categories describing the feedback.
   * @param args.notes - Free-text notes. Required when 'other' is among the categories.
   * @param options - Optional abort signal.
   * @returns The persisted feedback record.
   */
  public async submitFeedback(
    threadId: string,
    messageSequenceNum: number,
    args: {
      sentiment: FeedbackSentiment;
      categories: FeedbackCategory[];
      notes?: string | null;
    },
    options?: Partial<Options>,
  ): Promise<UserFeedbackRecord> {
    if (!options?.orgId) {
      throw Error("submitFeedback requires an Org ID, none was provided");
    }
    if (!Number.isInteger(messageSequenceNum) || messageSequenceNum < 0) {
      throw Error(
        `submitFeedback requires a non-negative integer messageSequenceNum, got ${messageSequenceNum}`,
      );
    }
    const requestUrl = this.#httpClient.constructUrl(
      `v1/ai/threads/${threadId}/messages/${messageSequenceNum}/feedback`,
    );

    const response = await this.#httpClient.post(requestUrl, {
      signal: options.abortSignal,
      headers: robotoHeaders({ orgId: options.orgId }),
      body: JSON.stringify({
        sentiment: args.sentiment,
        categories: args.categories,
        notes: args.notes ?? null,
      }),
    });

    return response.json<UserFeedbackRecord>();
  }

  /**
   * Get the caller's own feedback on the given thread.
   *
   * Used by the thread UI to mark assistant messages the caller has already rated.
   *
   * @param threadId - Thread to look up feedback for.
   * @param options - Optional abort signal.
   * @returns Array of feedback records submitted by the current caller.
   */
  public async getFeedbackForAgentThread(
    threadId: string,
    options?: Partial<Options>,
  ): Promise<UserFeedbackRecord[]> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/ai/threads/${threadId}/feedback`,
    );

    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
      headers: options?.orgId
        ? robotoHeaders({ orgId: options.orgId })
        : undefined,
    });

    return response.json<UserFeedbackRecord[]>();
  }

  /**
   * Submit results from client-side tool execution.
   */
  public async submitToolResults(
    threadId: string,
    request: SubmitToolResultsRequest,
    options?: Partial<Options>,
  ): Promise<void> {
    if (!options?.orgId) {
      throw Error("submitToolResults requires an Org ID, none was provided");
    }

    const requestUrl = this.#httpClient.constructUrl(
      `v1/ai/threads/${threadId}/tool-results`,
    );

    await this.#httpClient.post(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ orgId: options.orgId }),
      body: JSON.stringify(request),
    });
  }
}
